import {ReactNode} from 'react';

import {isNotNil, uniq} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetEmployeeOrgranisationalDataQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {TestIdProps} from 'shared/web';

import {RecordAuditTeamMember} from './RecordAuditTeamMember';

interface RecordAuditParticipantMemberProps extends TestIdProps {
  employeeId: string;
  name: string;
  // Profession id -> label, built once by the parent from the work-professions list.
  professionLabelById: ReadonlyMap<string, string>;
  // True while that shared professions list is still loading.
  isProfessionsLoading: boolean;
  action?: ReactNode;
}

/**
 * Participant card that shows the employee's own professions (read-only) instead of a manually
 * assigned role. Professions come from the employee's organisational data — the profession ids
 * across their branches, deduped and labelled via the parent's professions map.
 */
export function RecordAuditParticipantMember(props: RecordAuditParticipantMemberProps) {
  const {professionIds, isLoading} = useGetEmployeeOrgranisationalDataQuery(
    {employeeId: props.employeeId},
    {
      selectFromResult: ({data, isLoading: isOrgDataLoading}) => ({
        isLoading: isOrgDataLoading,
        professionIds: uniq(
          (data?.organisationalData?.branches ?? [])
            .map((branch) => branch?.professionId)
            .filter(isNotNil)
        ),
      }),
    }
  );

  const professionLabels = professionIds
    .map((professionId) => props.professionLabelById.get(professionId))
    .filter(isNotNil);

  // Fall back to a neutral placeholder chip so every participant card carries a chip and the
  // grid stays uniform (auditor / responsible cards always have one).
  const roleLabels = isNotNilOrEmpty(professionLabels)
    ? professionLabels
    : [i18n.t('general.labels.notSpecified')];

  return (
    <RecordAuditTeamMember
      data-testid={props['data-testid']}
      name={props.name}
      roleLabels={roleLabels}
      roleColorScheme="neutral"
      isLoading={isLoading || props.isProfessionsLoading}
      action={props.action}
    />
  );
}
